import moment from "moment";
import "moment/locale/fr.js";
import _ from "lodash";
import processorObject from "./processorObject.js";
import { functionnal } from "vincent-utils";
import DdnObject from "../utils/ddnObject.js";
import { getAgeFromDdn } from "./age.js";
import { days, motifConsts, patientFlags, rdvPotentiels, typeConsts, } from "../../cadendar-consts.js";
import { getPotentielFromType, getTypeAndPotentielFromMotifs, } from "./typesMotifs.js";
import { isNouveauPatient } from "./ancienPatient.js";
import { computeDuree } from "./computeDuree.js";
import { DateTime } from "luxon";
import { z } from "zod/v4";
import { DDNSchema } from "../../types/Schemas.js";
export const strategorPatientSchema = z.object({
    birthdate: DDNSchema,
    patient_flags: z.enum(patientFlags).nullable(),
    seen: z.boolean().nullable(),
    former_patient: z.boolean(),
    adr_id: z.string().min(2).nullable(),
});
const not = functionnal.not;
const hasFlag = (patient, flag) => {
    return !!patient.patient_flags && patient.patient_flags === flag;
};
const someHaveFlag = (patients, flag) => patients.some((patient) => hasFlag(patient, flag));
const getAveragePatientsAge = function (dayCasesArray) {
    "use strict";
    const sum = dayCasesArray.reduce((sum, obj) => {
        if ("patient_ddn" in obj && obj.patient_ddn) {
            sum += new DdnObject(obj.patient_ddn).getAge();
        }
        return sum;
    }, 0);
    if (sum === 0) {
        return 0;
    }
    const count = dayCasesArray.reduce((count, obj) => {
        if ("patient_ddn" in obj) {
            count += 1;
        }
        return count;
    }, 0);
    if (sum !== 0 && count === 0) {
        throw new Error("count === 0 dans le calculateur de moyenne d'âge du jour alors que sum !=0");
    }
    return sum / count;
};
const dayPatientsAreOlderThan = function (age) {
    return function (dayCasesArray) {
        //j'hésite entre les deux versions
        //var ageOfDayPatients = getAverageOldPatientsAge(dayCasesArray) || getAveragePatientsAge(dayCasesArray);
        const ageOfDayPatients = getAveragePatientsAge(dayCasesArray);
        return ageOfDayPatients > age;
    };
};
const dayHasFlag = function (flag) {
    return function (dayCasesArray) {
        return dayCasesArray.reduce((response, obj) => {
            if ("patient_flags" in obj) {
                if (obj.patient_flags.includes(flag)) {
                    return true;
                }
            }
            return response;
        }, false);
    };
};
const dayHasPatientWhichSatisfies = function (predicate) {
    "use strict";
    if (!_.isFunction(predicate)) {
        return;
    }
    return function (dayCasesArray) {
        if (!_.isArray(dayCasesArray)) {
            return;
        }
        return dayCasesArray.reduce(function (response, obj) {
            if (!response) {
                return predicate(obj);
            }
            else {
                return response;
            }
        }, false);
    };
};
//les rdvs familiaux sont les rdvs avec le même famille_id
//renvoie true si on a au moins 5 groupes de rdvs familiaux par exemple
const dayHasAtLeastXFamilyRdvs = (X) => (dayCasesArray) => {
    return (Object.values(_.groupBy(dayCasesArray, "famille_id")).filter((subArr) => subArr.length > 1).length > X);
};
const weekHasFlag = function (flag) {
    return function (arrayOfDayArrays) {
        return _.reduce(arrayOfDayArrays, function (result, array) {
            if (!result) {
                if (dayHasFlag(flag)(array)) {
                    result = true;
                }
            }
            return result;
        }, false);
    };
};
function isRdv(dayCase) {
    return "owner" in dayCase;
}
const weekHasXPatientsOrMoreWhichSatisfy = function (x, conditionsArray) {
    "use strict";
    if (!_.isNumber(x) || !Array.isArray(conditionsArray)) {
        throw new Error("weekHasMoreThanXPatientsWhichSatisfy called with wrong arguments");
    }
    return function (arrayOfDayArrays) {
        if (arrayOfDayArrays.length === 0) {
            return false;
        }
        const pred = functionnal.multiPredicatesToUniquePredicate(conditionsArray);
        const count = arrayOfDayArrays.reduce(function (answer, dayArray) {
            // if (!Array.isArray(dayArray)) {
            //   return;
            // }
            answer += dayArray.filter(isRdv).reduce(function (sum, rdv) {
                if (pred(rdv)) {
                    //console.log("rdv", rdv);
                    return sum + 1;
                }
                else {
                    return sum;
                }
            }, 0);
            return answer;
        }, 0);
        //console.log("count", count);
        return count >= x;
    };
};
//vérifie si le dayCasesArray fait partie des jours prochains daysArray, 0 est aujourd'hui, 1 est demain
const areComingDays = function (daysNumbers, now) {
    return function (dayCasesArray) {
        if (!Array.isArray(dayCasesArray) ||
            dayCasesArray.length === 0 ||
            !dayCasesArray[0]) {
            return false;
        }
        const comingDays = _.map(daysNumbers, function (dayNumber) {
            return moment(now).add(dayNumber, "days");
        });
        return !!_.find(comingDays, function (momentDay) {
            return momentDay.isSame(moment(dayCasesArray[0].date), "day");
        });
    };
};
const areComingOpenDays = (daysArray, now, openSaturday) => {
    //todo ajouter checks
    return (dayCasesArray) => {
        if (!Array.isArray(dayCasesArray) ||
            dayCasesArray.length === 0 ||
            !dayCasesArray[0]) {
            return;
        }
        const comingDays = _.map(daysArray, function (dayNumber) {
            return moment(now).add(dayNumber, "days");
        });
        const isOpenDay = (openSaturday) => {
            return (m) => {
                if (openSaturday) {
                    return m.day() !== 0;
                }
                else {
                    return m.day() !== 0 && m.day() !== 6;
                }
            };
        };
        while (comingDays.some(functionnal.not(isOpenDay(openSaturday)))) {
            for (let i = 0; i < comingDays.length; i++) {
                if (comingDays[i].day() === 0 || comingDays[i].day() === 6) {
                    for (let j = i; j < comingDays.length; j++) {
                        comingDays[j] = comingDays[j].add(1, "days");
                    }
                }
            }
        }
        return !!_.find(comingDays, function (momentDay) {
            return momentDay.isSame(moment(dayCasesArray[0].date), "day");
        });
    };
};
const isToday = (now) => (dayCasesArray) => {
    return areComingDays([0], now)(dayCasesArray);
};
const isAPastDay = (today) => (dayCasesArray) => {
    if (dayCasesArray.length === 0) {
        return false;
    }
    return (dayCasesArray[0] && moment(dayCasesArray[0].date).isBefore(today, "day"));
};
const isDayBefore = function (date) {
    return function (dayCasesArray) {
        if (dayCasesArray.length === 0) {
            return false;
        }
        return moment(dayCasesArray[0].date).isBefore(date);
    };
};
//attention, le numéro du block est zero based
const caseIsFromHoraireBlock = function (horaireBlockNumber, horaire) {
    return function (rdv) {
        if (!_.isObject(rdv)) {
            return;
        }
        const jour = days[rdv.date.getDay()];
        const bornes = horaire && horaire.horaire && horaire.horaire[jour];
        if (!bornes) {
            throw new Error("bornes not found in caseIsFromHoraireBlock");
        }
        if (horaireBlockNumber + 1 > bornes.length) {
            return false;
        }
        let blockStart = bornes[horaireBlockNumber].start, blockEnd = bornes[horaireBlockNumber].end;
        if (!blockStart || !blockEnd) {
            throw new Error("le block demandé ne correspond à rien");
        }
        return (rdv.hour * 60 + rdv.minute >= blockStart.hour * 60 + blockStart.minute &&
            rdv.hour * 60 + rdv.minute <= blockEnd.hour * 60 + blockEnd.minute);
    };
};
/**
 *
 * @param h {number}
 * @param m {number}
 * @returns {Function}
 */
const rdvIsAfterOrEqualHHourMMinute = function (h, m) {
    return function (rdv) {
        return h * 60 + m <= rdv.hour * 60 + rdv.minute;
    };
};
const _rdvHasFlag = (rdv, flag) => {
    return rdv.patient_flags === flag;
};
const rdvHasFlag = function (flag) {
    return function (rdv) {
        return rdv.tag === "rdv" && _rdvHasFlag(rdv, flag);
    };
};
const isNouveauPatientRdv = function (rdv) {
    return (rdv.tag === "rdv" &&
        (rdv.type === typeConsts.PCONSULT || rdv.type === typeConsts.URGENCE));
};
function omit(stuff) {
    if (Array.isArray(stuff)) {
        return stuff.map(omit);
    }
    else {
        return { ...stuff, isOmitted: true };
    }
}
function omitWeek(weekArray) {
    return weekArray.map(omitDay);
}
function omitDay(dayArray) {
    return dayArray.map((obj) => ({ ...obj, isOmitted: true }));
}
const markOmitted = (obj) => ({ ...obj, isOmitted: true });
const omitCasesFromDayThatSatisfy = function (cond) {
    return function (dayArray) {
        return dayArray.map(function (rdv) {
            if (cond(rdv)) {
                return { ...rdv, isOmitted: true };
            }
            return rdv;
        });
    };
};
const omitCasesFromWeekThatSatisfy = function (cond) {
    return function (weekArray) {
        return weekArray.map(function (dayArray) {
            return omitCasesFromDayThatSatisfy(cond)(dayArray);
        });
    };
};
const omitBlock = function (blockNumber, horaire) {
    return function (casesDayArray) {
        return casesDayArray.map(function (obj) {
            if (caseIsFromHoraireBlock(blockNumber, horaire)(obj)) {
                return { ...obj, isOmitted: true };
            }
            return obj;
        });
    };
};
const dayIsDayWeek = function (dayNameInFrench) {
    return function (dayCasesArray) {
        if (dayCasesArray.length !== 0 && dayCasesArray[0]) {
            if (dayCasesArray[0].date) {
                return moment(dayCasesArray[0].date).format("dddd") === dayNameInFrench;
            }
        }
        return false;
    };
};
const patientIsOlderThan = function (age, patient) {
    "use strict";
    return (!!patient &&
        !!patient.patient_ddn &&
        new DdnObject(patient.patient_ddn).getAge() > age);
};
const patientIsYoungerThan = function (age, kase) {
    return (kase.tag === "rdv" &&
        !!kase.birthdate &&
        new DdnObject(kase.birthdate).getAge() < age);
};
const isNotClosedCase = (a) => (a.tag === "rdv" && a.type !== typeConsts.FERME) || a.tag === "empty";
const rdvCaseIsAvailable = (a) => a.tag === "empty" ||
    ("type" in a &&
        (a.type === typeConsts.ANNTARD ||
            a.type === typeConsts.ANN ||
            a.type === typeConsts.LAPIN ||
            a.type === typeConsts.MANQRET));
//remplissage du jour en retirant les rdv closed
const getDayFillRatio = (dayArray) => {
    const totalOpenTime = getTotalOpenTime(dayArray);
    if (totalOpenTime === 0) {
        return 0;
    }
    return (1 - getRdvsDureeSum(dayArray.filter(rdvCaseIsAvailable)) / totalOpenTime);
};
const getTotalOpenTime = (dayArray) => getRdvsDureeSum(dayArray.filter((c) => c.tag === "empty"));
const getRdvsDureeSum = (rdvArray) => rdvArray.map((a) => a.duration).reduce((a, b) => a + b, 0);
const getRentableRatio = (dayArray) => {
    const totalOpenTime = getTotalOpenTime(dayArray);
    if (totalOpenTime === 0) {
        return 0;
    }
    return getRdvsDureeSum(dayArray.filter(rdvIsRentable)) / totalOpenTime;
};
const rdvIsRentable = (rdv) => "type" in rdv && getPotentielFromType(rdv.type) === rdvPotentiels.HPRDV;
const dayCanAcceptNonRentableRdv = (dayArray, ratioGoal, rdvProposed) => {
    const dureeRdvsRentables = getRdvsDureeSum(dayArray.filter(rdvIsRentable));
    const emptyTimeAvailableAfterRdvAdded = getRdvsDureeSum(dayArray.filter(rdvCaseIsAvailable)) - rdvProposed.duree;
    //est-ce que le temps des rdvs rentables plus le temps restant après ajout du rdv proposé
    //divisé par le temps total des rdvs ouverts et occupés
    //est supérieur ou égal au ratio goal ?
    return ((dureeRdvsRentables + emptyTimeAvailableAfterRdvAdded) /
        getRdvsDureeSum(dayArray.filter(isNotClosedCase)) -
        ratioGoal >=
        0);
};
const isAgainstBlockPolicy = (ratioGoal, rdvCanvas, now, unlockedDays, openSaturday) => {
    return (dayArray) => {
        //un tableau vide (ou déjà filtré ) n'est pas contre la politique des blocs
        if ((Array.isArray(dayArray) && dayArray.length === 0) ||
            dayArray.filter((rdv) => !rdv.isOmitted).length === 0) {
            return false;
        }
        //pour les prochaines jours, on n'applique pas les blocs
        if (areComingOpenDays(_.range(unlockedDays), now, openSaturday)(dayArray)) {
            return false; //on ne vérifie plus les blocks pour les jours qui viennent
        }
        //un rdv rentable ne contrevient jamais aux blocs
        if (rdvIsRentable(rdvCanvas)) {
            return false;
        }
        if (!rdvIsRentable(rdvCanvas) &&
            dayCanAcceptNonRentableRdv(dayArray.filter((rdv) => !rdv.isOmitted), ratioGoal, rdvCanvas)) {
            return false; //le rdv n'est pas rentable mais on peut encore en accepter
        }
        return true;
    };
};
const buildWeekFilters = function (patients, _motifs, now) {
    "use strict";
    const weekProcessor = new processorObject("week");
    if (patients.some((patient) => hasFlag(patient, "pv"))) {
        //weekFilters.push(filterCreator(weekHasFlag("PV"), omit, 'week'));
        weekProcessor.addFilter(weekHasFlag("pv"), omitWeek);
    }
    //si patient VIP, on saute le reste
    if (patients.some((patient) => hasFlag(patient, "vip"))) {
        return weekProcessor;
    }
    //si le patient est nouveau et adressé on zappe les autres filtres
    if (patients.length === 1 &&
        isNouveauPatient(patients[0]) &&
        patients[0].isAdresséParNormal) {
        return weekProcessor;
    }
    //pour les rdvs multiples (et les rdvs solos !), si un des patients a moins de 30 ans, pas plus de 2 patients de moins de 30 ans dans la semaine
    const patientMinAge = Math.min(...patients
        .map((patient) => patient.birthdate)
        .map(functionnal.curry2(getAgeFromDdn)(now)));
    if (patientMinAge < 30 && patients.every(isNouveauPatient)) {
        weekProcessor.addFilter(weekHasXPatientsOrMoreWhichSatisfy(2, [
            isNouveauPatientRdv,
            patientIsYoungerThan.bind(null, 30),
        ]), omitWeek);
    }
    //if (patientAge > 16 && patientAge < 35 && isNouveauPatient(patient)){
    //    weekProcessor.addFilter(weekHasXPatientsOrMoreWhichSatisfy(7, [isNouveauPatientRdv, patientIsOlderThan.bind(null, 16),
    //        patientIsYoungerThan.bind(null, 35)]),omit);
    //}
    //
    //if (patientAge > 16 && patientAge < 40 && isNouveauPatient(patient)){
    //    weekProcessor.addFilter(weekHasXPatientsOrMoreWhichSatisfy(9, [isNouveauPatientRdv, patientIsOlderThan.bind(null, 16),
    //        patientIsYoungerThan.bind(null, 40)]),omit);
    //}
    //pas plus de 1 nouvel enfant de moins de vingt ans par semaine
    if (patientMinAge > 0 &&
        patientMinAge < 20 &&
        patients.every(isNouveauPatient)) {
        weekProcessor.addFilter(weekHasXPatientsOrMoreWhichSatisfy(1, [
            isNouveauPatientRdv,
            patientIsYoungerThan.bind(null, 20),
        ]), omitWeek);
    }
    if (patients.some((patient) => hasFlag(patient, "pp"))) {
        weekProcessor.addFilter(weekHasXPatientsOrMoreWhichSatisfy(1, [
            rdvIsAfterOrEqualHHourMMinute(18, 0),
            rdvHasFlag("pp"),
        ]), omitCasesFromWeekThatSatisfy(rdvIsAfterOrEqualHHourMMinute(18, 0)));
    }
    //todo interdire les pv le soir
    //todo: faire quelque chose avec les motifs
    //testForEmptyCase(weekFilters);
    //return weekFilters;
    return weekProcessor;
};
const buildDayFilters = function (patients, motifs, startDate, horaire, now, prefs, setDuree) {
    "use strict";
    const dayProcessor = new processorObject("day");
    if (!patients || patients.length === 0) {
        return dayProcessor;
    }
    if (!prefs) {
        return dayProcessor;
    }
    const firstPatient = patients[0];
    if (!firstPatient.birthdate) {
        throw new Error("patient without ddn in filterCandidatesByStrategy");
    }
    const patientAge = new DdnObject(firstPatient.birthdate).getAge();
    //const now = new Date();
    //const patientMinAge = Math.min(...patients.map(patient => (patient.ddn)).map(functionnal.curry2(getAgeFromDdn)(now)));
    //on ne retient pas les jours passés !
    dayProcessor.addFilter(isAPastDay(now), omitDay);
    if (_.isDate(startDate)) {
        dayProcessor.addFilter(isDayBefore(startDate), omitDay);
    }
    //pas de rdv en auto le samedi après midi
    dayProcessor.addFilter(dayIsDayWeek("samedi"), omitBlock(1, horaire), "Pas de rdv le Samedi");
    //si le patient est vip, on n'applique pas les autres filtres
    if (patients.some((patient) => hasFlag(patient, "vip"))) {
        return dayProcessor;
    }
    //mis au début pour couper le reste et que le filtre apparaisse en premier dans les raisons.
    if (patients.some((patient) => hasFlag(patient, "bl"))) {
        dayProcessor.addFilter(function () {
            return true;
        }, omitDay, "Ce Patient n'a plus le droit d'avoir de nouveaux rdvs");
    }
    //pas de NP le Samedi
    if (patients.every((patient) => isNouveauPatient(patient))) {
        dayProcessor.addFilter(dayIsDayWeek("samedi"), omitDay, "Pas de nouveau patient le Samedi");
    }
    //si le patient est nouveau et adressé on zappe les autres filtres
    if (patients.every((patient) => isNouveauPatient(patient)) &&
        patients.some((patient) => patient.isAdresséParNormal)) {
        return dayProcessor;
    }
    //attention les cadendar-filters qui se basent sur la journée doivent se passer avant les filtres qui virent des blocs
    if (patients.some((patient) => hasFlag(patient, "pp"))) {
        //dayFilters.push(filterCreator(dayHasFlag("PP"), omit, 'day'));
        dayProcessor.addFilter(dayHasFlag("pp"), omitDay);
        dayProcessor.addFilter(dayHasFlag("pv"), omitDay);
        dayProcessor.addFilter(dayIsDayWeek("samedi"), omitDay); //pas de pp le samedi
    }
    if (someHaveFlag(patients, "pv")) {
        //dayFilters.push(filterCreator(dayHasFlag("PP"), omit, 'day'));
        dayProcessor.addFilter(dayHasFlag("pp"), omitDay);
        dayProcessor.addFilter(dayIsDayWeek("samedi"), omitDay);
        dayProcessor.addFilter(dayIsDayWeek("samedi"), omitDay); //pas de pv le samedi
    }
    //si le motif contient urgence, on ne retient qu'aujourd'hui et demain
    if (motifs.includes(motifConsts.URGENCE)) {
        //dayFilters.push(filterCreator(not(areComingDays([0, 1], now)), omit, 'day'));
        dayProcessor.addFilter(not(areComingDays([0, 1], now)), omitDay, "Une urgence ne peut être vue qu'aujourd'hui ou demain");
        //c'est une urgence, on n'applique pas les autres filtres
        return dayProcessor;
    }
    //DESACTIVE POUR L'INSTANT
    //pour un nouveau patient majeur non adressé il doit être plus vieux que la moyenne des patients du jour
    // if (false && isNouveauPatient(patient) && patientAge > 18 && !patient.isAdresséParNormal()) {
    //   dayProcessor.addFilter(dayPatientsAreOlderThan(patientAge), omit);
    // }
    // //Pas plus d'un NP de 0-30 par jour
    // if (patients.length === 1 &&  isNouveauPatient(patients[0]) && patientAge > 0 && patientAge < 30) {
    //   dayProcessor.addFilter(dayHasPatientWhichSatisfies(functionnal.multiPredicatesToUniquePredicate([isNouveauPatientRdv, patientIsOlderThan.bind(null, 18),
    //     patientIsYoungerThan.bind(null, 30)])), omit);
    // }
    // //Pas plus d'un NP de 0-30 par jour
    // const patientMinAge = Math.min(...patients.map(patient => (patient.ddn)).map(functionnal.curry2(getAgeFromDdn)(now)));
    // if (patientMinAge < 30 && patients.every(isNouveauPatient)) {
    //   dayProcessor.addFilter(dayHasPatientWhichSatisfies(patientIsYoungerThan.bind(null, 30)), omit);
    // }
    //les nouveaux patients majeurs n'ont pas de rdv le mercredi après midi
    //DESACTIVE
    // if (false && isNouveauPatient(patient) && patientAge > 18 && !patient.isAdresséParNormal()) {
    //   dayProcessor.addFilter(dayIsDayWeek("mercredi"), omitBlock(1), "Uniquement des enfants de moins de 18 ans le mercredi AM");
    // }
    //pas de rdv le Samedi
    dayProcessor.addFilter(dayIsDayWeek("samedi"), omitDay);
    //si ce n'est pas une urgence et que le patient n'est pas VIP, pas de rdv aujourd'hui
    if (motifs.includes(motifConsts.URGENCE) && !someHaveFlag(patients, "vip")) {
        //dayFilters.push(filterCreator(isToday,omit, 'day'));
        dayProcessor.addFilter(isToday(now), omitDay);
    }
    //todo: faire quelque chose avec les motifs
    //return dayFilters;
    const rdvCanvas = getRdvCanvas(motifs, patients, prefs, now, setDuree);
    dayProcessor.addFilter(isAgainstBlockPolicy((prefs.blocs_ratio || 0) / 100, //si pas de blocs, on met un ratio cible à zéro //TODO à tester
    rdvCanvas, now, prefs.blocs_unlocked_days || 100, //si pas de blocs, on met un jour arbitraire à 100 //TODO à tester
    false), omitDay, "blocs");
    //plusieurs patients, c'est une famille
    if (patients.length > 1) {
        dayProcessor.addFilter(dayHasAtLeastXFamilyRdvs(4), omitDay, "déjà des rdvs familiaux aujourd'hui");
    }
    return dayProcessor;
};
const removeOmittedCases = function (objectArr) {
    return objectArr.map((arr) => arr.filter((obj) => !obj.isOmitted));
};
const getRdvCanvas = (motifs, patients, prefs, now, setDuree) => {
    const potentielAndType = getTypeAndPotentielFromMotifs(motifs, patients[0]);
    const type = potentielAndType.type;
    if (!type) {
        throw new Error("no type in getRdvCanvas");
    }
    const duree = setDuree ||
        computeDuree(motifs, patients, prefs.duree_min_rdv, prefs.duree_default_rdv || prefs.duree_min_rdv, now, setDuree);
    return {
        type,
        duree,
    };
};
const filterCases = function (semaineArray, patients, motifs, startDate, horaire, now, prefs, setDuree) {
    "use strict";
    if (!horaire) {
        return {
            semaineArray: [],
            reasons: [],
        };
    }
    //le weekProcessor permet de retirer des semaines entières basées sur le patient et les motifs (pour l'instant les motifs ne sont pas utilisés)
    const weekProcessor = buildWeekFilters(patients, motifs, now);
    //le dayProcessor permet de retirer des jours entiers
    const dayProcessor = buildDayFilters(patients, motifs, startDate, horaire, now, prefs, setDuree);
    let processed = weekProcessor.process(semaineArray);
    processed = dayProcessor.process(processed);
    const weekFiltered = removeOmittedCases(processed); //on retire les cases qui ont été marquée à retirer
    const withoutTodayPassedCases = weekFiltered.map((dayRdvs) => dayRdvs.filter((kase) => DateTime.fromJSDate(kase.date)
        .plus({ minute: kase.hour * 60 + kase.minute })
        .toJSDate()
        .getTime() > now.getTime()));
    //TODO: variable globale dégueulasse, à changer
    const reasons = dayProcessor.getMessages();
    return {
        semaineArray: withoutTodayPassedCases,
        reasons,
    };
};
const test = {
    getDayFillRatio,
    dayCanAcceptNonRentableRdv,
    areComingDays,
    areComingOpenDays,
};
export { filterCases, test, getRentableRatio, getDayFillRatio };
//# sourceMappingURL=filterCandidatesByStrategy.js.map